/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// components
import { Form } from '@/components/forms/form';
import { PromoCodeFields } from './promo-code-field';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, ChevronDown, ChevronUp, X as CloseIcon, TriangleAlert } from 'lucide-react';
//hooks
import { usePromoCodeActions } from '@/hooks/use-promo-code-actions';

//types
import { cn } from '@/lib/utils';
import { createPromoCodeFormSchema, type PromoCodeFormData } from './index';
import { type PromoCodeFormProps } from './types';
import { useTranslation } from 'react-i18next';
import {
    CouponErrorCode,
    errorI18nKey,
    titleI18nKey,
    type CouponErrorCode as CouponErrorCodeType,
} from '@/lib/coupon-fault-map';
//import { usePathname } from '@/hooks/use-pathname';

/**
 * Defensive chip-render filter mirroring the silent-accept detection in
 * `action.promo-code-add.tsx`. SCAPI sometimes leaves a coupon item on
 * the basket after a non-applicable apply (200 with `statusCode !=
 * 'applied'`), and the on-apply handler may not have run for items
 * written before the fix shipped. Without this filter the form would
 * render a chip for a coupon that did NOTHING to the basket.
 *
 * Primary signal: `statusCode === 'applied'` — SCAPI sets that exactly
 * when the promo engine produced an adjustment. Anything else
 * (`no_applicable_promotion`, `no_active_promotion`, `coupon_disabled`,
 * any expired / limit-exceeded variant) means the coupon did not apply.
 *
 * Secondary signal (defense in depth): even when `statusCode === 'applied'`
 * we also require a visible effect — at least one priceAdjustment
 * carrying its couponCode at order / product / shipping level, or any
 * bonusDiscountLineItem on the basket. A `statusCode: applied` with no
 * adjustment would be a SCAPI bug, but the check costs nothing.
 *
 * Permissive fallback when we have no basket to walk (e.g. mid-render
 * before the basket loader settles): trust `statusCode === 'applied'`
 * alone so we don't flicker the chip away during normal loading.
 */
function isCouponActuallyApplied(
    item: { code?: string; valid?: boolean; statusCode?: string },
    basket: PromoCodeFormProps['basket']
): boolean {
    if (item.valid === false) {
        return false;
    }
    if (typeof item.statusCode === 'string' && item.statusCode !== 'applied') {
        return false;
    }
    if (!basket || typeof item.code !== 'string' || item.code.length === 0) {
        return true;
    }
    const target = item.code.toLowerCase();
    const matches = (adj: { couponCode?: string }): boolean =>
        typeof adj.couponCode === 'string' && adj.couponCode.toLowerCase() === target;

    if ((basket.orderPriceAdjustments ?? []).some(matches)) {
        return true;
    }
    if ((basket.productItems ?? []).some((p) => (p.priceAdjustments ?? []).some(matches))) {
        return true;
    }
    const shippingItems = (basket as { shippingItems?: Array<{ priceAdjustments?: { couponCode?: string }[] }> })
        .shippingItems;
    if ((shippingItems ?? []).some((s) => (s.priceAdjustments ?? []).some(matches))) {
        return true;
    }
    const bonusItems = (basket as { bonusDiscountLineItems?: unknown[] }).bonusDiscountLineItems;
    if ((bonusItems ?? []).length > 0) {
        return true;
    }
    return false;
}

/**
 * Reads the persisted `c_couponLastError` enum off the basket. SCAPI may
 * surface the attribute either as a top-level `c_*` field or nested under
 * `custom.*` depending on the call path — tolerate both. Returns null when
 * unset or when the value is not a recognized enum (defensive against
 * stale data after a schema change).
 */
function readPersistedCouponError(basket: PromoCodeFormProps['basket']): CouponErrorCodeType | null {
    if (!basket) return null;
    const raw =
        (basket as { c_couponLastError?: unknown }).c_couponLastError ??
        (basket as { custom?: { couponLastError?: unknown } }).custom?.couponLastError ??
        null;
    if (typeof raw !== 'string' || raw.length === 0) {
        return null;
    }
    return (Object.values(CouponErrorCode) as string[]).includes(raw) ? (raw as CouponErrorCodeType) : null;
}

/**
 * PromoCodeForm component that provides an accordion-based interface for applying promo codes to a shopping basket.
 *
 * This component renders as a collapsible accordion containing a form for entering and submitting promo codes.
 * It handles form validation, submission, and displays appropriate success/error feedback through toasts.
 * The form automatically resets and closes the accordion on successful submission.
 *
 * @param basketId - Optional basket ID to associate the promo code with. If not provided, form submission will
 *                   show an error.
 *
 * @returns JSX element containing the promo code form wrapped in an accordion
 *
 * @example
 * ```tsx
 * // Basic usage with basket ID
 * <PromoCodeForm basketId="basket-123" />
 *
 * // Usage without basket ID (will show error on submit)
 * <PromoCodeForm />
 * ```
 *
 */
export const PromoCodeForm = ({ basket }: PromoCodeFormProps) => {
    const { t } = useTranslation('cart');
    const basketId = basket?.basketId;
    const [isOpen, setIsOpen] = useState(false);
    const { applyPromoCode, removePromoCode, removeFetcher, applyFetcher } = usePromoCodeActions(basketId);

    // Inline status banner state. Success is not surfaced through the form's
    // field error (that's error-only) and remove has no field to attach to
    // at all, so both flows funnel into a single Alert rendered beneath the
    // accordion trigger. The Alert visual matches the Storefront Next design
    // system spec (Figma node 37470:86929): bold title row + regular
    // description, top border accent, drop shadow, icon.
    const [statusMessage, setStatusMessage] = useState<{
        kind: 'success' | 'error';
        title: string;
        text: string;
    } | null>(null);

    // Remembers the last code the shopper submitted so the success and
    // error copy (which both use "Promo code {{code}}..." interpolation)
    // can render it even after `form.reset()` wipes the input on success
    // and after the fetcher's `formData` clears when it goes back to idle.
    const [lastSubmittedCode, setLastSubmittedCode] = useState('');

    useEffect(() => {
        if (removeFetcher.data) {
            if (removeFetcher.data.success) {
                setStatusMessage({
                    kind: 'success',
                    title: t('promoCode.removeSuccessTitle'),
                    text: t('promoCode.removeSuccessMessage'),
                });
            } else if (removeFetcher.data.error) {
                setStatusMessage({
                    kind: 'error',
                    title: t('promoCode.removeErrorTitle'),
                    text: t('promoCode.removeErrorMessage'),
                });
            }
        } else {
            setStatusMessage(null);
        }
    }, [removeFetcher.data, t]);

    const schema = useMemo(() => createPromoCodeFormSchema(t as (key: string) => string), [t]);

    const form = useForm<PromoCodeFormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            code: '',
        },
    });

    /**
     * Handles the response from the promo code application API call.
     *
     * Branches the error message by `error.code` so each typed SCAPI fault
     * (`INVALID_COUPON_CODE`, `COUPON_CODE_EXPIRED`, `COUPON_CODE_NOT_APPLICABLE`,
     * `COUPON_CODE_ALREADY_IN_BASKET`, `TOO_MANY_ATTEMPTS`) renders its own
     * inline copy via the centralised `errorI18nKey` map. Unknown / unmapped
     * faults fall back to the generic message. The action never echoes the
     * submitted code so the inline render is safe to surface verbatim.
     *
     * @dependencies applyFetcher.data, form
     */
    useEffect(() => {
        if (applyFetcher.data) {
            // Cast once — `t()` from the namespace-typed useTranslation('cart')
            // can't statically see the dynamic namespace-qualified keys the
            // fault map returns (`cart:promoCode.error.*`). Runtime is safe
            // because the helpers only ever return keys we own.
            const tDyn = t as (key: string, opts?: Record<string, string>) => string;
            if (applyFetcher.data.success) {
                form.clearErrors('code');
                form.reset({ code: '' });
                setStatusMessage({
                    kind: 'success',
                    title: t('promoCode.successTitle'),
                    text: tDyn('promoCode.successMessage', { code: lastSubmittedCode }),
                });
            } else {
                const code = applyFetcher.data.error?.code as CouponErrorCodeType | undefined;
                const isKnownCouponError =
                    typeof code === 'string' && (Object.values(CouponErrorCode) as string[]).includes(code);
                const titleKey = isKnownCouponError ? titleI18nKey(code) : 'cart:promoCode.errorTitle';
                const bodyKey = isKnownCouponError
                    ? errorI18nKey(code, { hasCode: true })
                    : 'cart:promoCode.errorMessage';

                // Single source of truth for API-error messaging: the inline
                // Alert. We intentionally do NOT call `form.setError` here —
                // that would render a second copy of the same message via
                // FormMessage under the input (the "two messages" bug).
                // Client-side zod validation (min-length) still uses setError
                // through the resolver and renders through FormMessage as
                // usual, so validation feedback is unaffected.
                setStatusMessage({
                    kind: 'error',
                    title: tDyn(titleKey),
                    text: tDyn(bodyKey, { code: lastSubmittedCode }),
                });
            }
        }
    }, [applyFetcher.data, form, t, lastSubmittedCode]);

    /**
     * Surfaces the persisted basket-side coupon error (CART-11
     * `c_couponLastError`) inline when the form mounts or the basket
     * changes. This is how a failure survives a redirect — the loader
     * reads the attribute, the form re-renders the same message without
     * a toast (toasts only fire on the live submit path; the redirect
     * path already gave the shopper one notification).
     */
    const persistedError = readPersistedCouponError(basket);
    const watchedPromoCode = form.watch('code');
    const hasAppliedCouponItems = useMemo(
        () => (basket?.couponItems ?? []).some((item) => isCouponActuallyApplied(item, basket)),
        [basket]
    );
    const persistedErrorStorageKey = useMemo(() => {
        if (!persistedError) return null;
        const basketScope = basketId ?? 'no-basket';
        return `sfcc_seen_err_${basketScope}_${persistedError}`;
    }, [basketId, persistedError]);

    useEffect(() => {
        if (!persistedError) return;
        if (hasAppliedCouponItems) {
            form.clearErrors('code');
            return;
        }
        if (applyFetcher.data?.success) {
            form.clearErrors('code');
            return;
        }
        if (applyFetcher.data && !applyFetcher.data.success) return;
        if (!persistedErrorStorageKey) return;

        // 2. If the user has already seen this server error, suppress it and wipe the form error
        if (sessionStorage.getItem(persistedErrorStorageKey) === 'true') {
            form.clearErrors('code');
            setStatusMessage(null);
            return;
        }

        // 3. First time seeing it? Set the form validation error cleanly
        form.setError('code', {
            type: 'manual',
            message: String((t as (key: string) => string)(errorI18nKey(persistedError, { hasCode: false }))),
        });

        // 4. Mark it as seen in the browser session storage
        sessionStorage.setItem(persistedErrorStorageKey, 'true');
    }, [persistedError, hasAppliedCouponItems, persistedErrorStorageKey, applyFetcher.data, form, t]);

    // 5. If the shopper edits the code field, clear only the current
    // persisted-error marker so a fresh submit can surface a fresh failure.
    useEffect(() => {
        if (!persistedErrorStorageKey) return;
        if (watchedPromoCode && watchedPromoCode.trim() !== '') {
            sessionStorage.removeItem(persistedErrorStorageKey);
            form.clearErrors('code');
            setStatusMessage(null);
        }
    }, [watchedPromoCode, persistedErrorStorageKey, form]);

    /**
     * Handles form submission for applying a promo code.
     *
     * This function is called when the form is submitted and performs the following:
     * 1. Validates that a basket ID is available
     * 2. If no basket ID, sets a form error and returns early
     * 3. If basket ID exists, calls the applyPromoCode function with the entered code
     *
     * @param data - The validated form data containing the promo code
     * @param data.code - The promo code string entered by the user
     */
    const handleSubmit = form.handleSubmit((data) => {
        if (!basketId) {
            form.setError('code', {
                type: 'manual',
                message: t('promoCode.noBasketMessage'),
            });
            return;
        }

        // Capture the entered code before submit so the success/error
        // banner can interpolate it via `{{code}}` — the form field is
        // reset on success and the fetcher's `formData` is only present
        // while state === 'submitting'.
        setLastSubmittedCode(data.code);
        applyPromoCode(data.code);
    });
    const promoPanelId = 'promo-code-panel';

    return (
        <div className="flex w-full flex-col gap-3 py-3">
            <button
                type="button"
                onClick={() => {
                    if (isOpen) {
                        // Closing the accordion: reset form state and any
                        // lingering status banner so re-opening starts clean.
                        form.reset();
                        setStatusMessage(null);
                    }
                    setIsOpen((prev) => !prev);
                }}
                aria-expanded={isOpen}
                aria-controls={promoPanelId}
                className="flex w-full items-center justify-start gap-2 py-1 text-left">
                <span className="text-sm font-normal text-foreground">{t('promoCode.accordionTitle')}</span>
                {isOpen ? (
                    <ChevronUp aria-hidden="true" className="size-5 text-foreground" />
                ) : (
                    <ChevronDown aria-hidden="true" className="size-5 text-foreground" />
                )}
            </button>

            {isOpen ? (
                <div id={promoPanelId} className="pt-1">
                    <Form {...form}>
                        <form onSubmit={(e) => void handleSubmit(e)} data-testid="promo-code-form">
                            <PromoCodeFields form={form} applyFetcher={applyFetcher} />
                        </form>
                    </Form>
                </div>
            ) : null}

            {statusMessage ? (
                <Alert
                    role={statusMessage.kind === 'error' ? 'alert' : 'status'}
                    aria-live="polite"
                    data-testid={
                        statusMessage.kind === 'success' ? 'promo-code-success-message' : 'promo-code-error-message'
                    }
                    /*
                     * Figma spec (node 37470:86929):
                     *   - white card with 2px top border in the state's accent
                     *     colour (destructive for error, success for success),
                     *     the other three sides borderless
                     *   - 4px rounded corners, drop-shadow-lg
                     *   - p-4 (16px all sides), gap-3 (12px between icon and body)
                     *   - icon: 16px lucide, currentColor = accent
                     *   - body column: gap-1, bold title (text-sm), muted description
                     */
                    className={cn(
                        // Base card per design: 4px radius (`--radius-xs`),
                        // white surface, side/bottom borders hidden, 4px
                        // vertical gap between title and description
                        // (overrides Alert's default 2px `gap-y-0.5`).
                        'gap-y-1 rounded-xs border-transparent bg-card p-4 shadow-lg',
                        statusMessage.kind === 'success'
                            ? 'border-t-2 border-t-success text-success *:data-[slot=alert-description]:text-muted-foreground'
                            : 'border-t-2 border-t-destructive text-destructive *:data-[slot=alert-description]:text-muted-foreground'
                    )}>
                    {statusMessage.kind === 'success' ? (
                        <CheckCircle2 aria-hidden="true" />
                    ) : (
                        <TriangleAlert aria-hidden="true" />
                    )}
                    <AlertTitle className="line-clamp-none min-h-0 text-sm font-semibold text-foreground">
                        {statusMessage.title}
                    </AlertTitle>
                    <AlertDescription>{statusMessage.text}</AlertDescription>
                </Alert>
            ) : null}

            {(() => {
                const appliedCouponItems = (basket?.couponItems ?? []).filter((item) =>
                    isCouponActuallyApplied(item, basket)
                );
                if (appliedCouponItems.length === 0) {
                    return null;
                }
                return (
                    <div className="space-y-2 pt-1">
                        {appliedCouponItems.map((item) => {
                            const matchingAdjustment = (basket?.orderPriceAdjustments ?? []).find(
                                (adj) => adj.couponCode === item.code && !!adj.itemText
                            );
                            const displayLabel = matchingAdjustment?.itemText ?? item.code;
                            return (
                                <div key={item.couponItemId} className="flex items-center justify-between py-1">
                                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-destructive">
                                        <span>{displayLabel}</span>
                                        <button
                                            type="button"
                                            aria-label="Remove promo code"
                                            className="inline-flex items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                                            onClick={() => {
                                                if (item.couponItemId) {
                                                    removePromoCode(item.couponItemId);
                                                }
                                            }}>
                                            <CloseIcon className="size-4" />
                                        </button>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}
        </div>
    );
};
